import { FC, memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { TableBody as Body } from '@mui/material';

import { Badge, Text } from '../../../shared/uiComponents';
import { CustomTableRow, RowTableCell } from '../../../shared/uiComponents/Table/tableHelpers';
import AuthorizationDetailedInfo from '../authorizationDetailedInfo';
import { findExpiredInfoColor } from '../helpers';
import { BCBARowRendererProps } from '../types';

import { AuthorizationDetailsProperties } from '../../../redux/API/ClientAPIHelpers/userClientProperties';

const sortByStartDate = (a: AuthorizationDetailsProperties, b: AuthorizationDetailsProperties) => {
	const c = new Date(a.startDate);
	const d = new Date(b.startDate);
	if (c < d) return 1;
	if (c > d) return -1;

	return 0;
};

const sortByStartDateDesc = (a: AuthorizationDetailsProperties, b: AuthorizationDetailsProperties) => {
	const c = new Date(a.startDate);
	const d = new Date(b.startDate);
	if (c > d) return 1;
	if (c < d) return -1;

	return 0;
};

export const TableBody: FC<BCBARowRendererProps> = memo(({ data }) => {
	const activeRows = useMemo(
		() =>
			data
				.filter((dataItem) => dataItem.authorizations.some((x) => !dayjs().isAfter(x.endDate)))
				.map((row, index) => (
					<CustomTableRow key={index}>
						<RowTableCell>
							<Link to={`/clients/client/${row.client.id}`} target="_blank">
								{row.client.fullName}
							</Link>
						</RowTableCell>
						<RowTableCell>
							{Boolean(row.authorizations) &&
								[...row.authorizations]
									.sort(sortByStartDateDesc)
									.map((item, authIndex) => (
										<Badge
											key={`${item.startDate}-${item.endDate}-${authIndex}`}
											label={`${dayjs(item.startDate).format('MM-DD-YYYY')} - ${dayjs(item.endDate).format(
												'MM-DD-YYYY',
											)}`}
											sizeType="sm"
											type="success"
										/>
									))}
						</RowTableCell>
						<RowTableCell>
							<AuthorizationDetailedInfo info={row} />
						</RowTableCell>
					</CustomTableRow>
				)),
		[data],
	);
	const expiredRows = useMemo(
		() =>
			data
				.filter((dataItem) => dataItem.authorizations.every((x) => dayjs().isAfter(x.endDate)))
				.map((row, index) => (
					<CustomTableRow key={index}>
						<RowTableCell>
							<Text title={row.client.fullName as string} className="paddingLeft16" />
						</RowTableCell>
						<RowTableCell>
							{Boolean(row.authorizations) &&
								[...row.authorizations]
									.sort(sortByStartDate)
									.map((item, authIndex) => (
										<Badge
											key={`expired-${item.startDate}-${item.endDate}-${authIndex}`}
											label={`${dayjs(item.startDate).format('MM-DD-YYYY')} - ${dayjs(item.endDate).format(
												'MM-DD-YYYY',
											)}`}
											sizeType="sm"
											type={findExpiredInfoColor({ endDate: item.endDate })}
										/>
									))}
						</RowTableCell>
						<RowTableCell>
							<AuthorizationDetailedInfo info={row} />
						</RowTableCell>
					</CustomTableRow>
				)),
		[data],
	);

	return (
		<Body>
			{activeRows}
			{expiredRows}
		</Body>
	);
});
TableBody.displayName = 'TableBody';
